import {Component, OnInit} from '@angular/core';
import {
    MxButtonModel,
    MxCustomizer,
    MxFormCustomizerEvent,
    ObjectMetaData,
    OnBoFormMetadataReady,
    OnBoFormReady
} from 'mx-core';
import {FormGroup, Validators} from '@angular/forms';
import {PrlTranslateService} from '../../../../../shared/services/prl-translate.service';
import {AffiliateBalanceMxObj} from '../../../../../../../../mx-core/src/generated/generated-core.model';
import {PrlFinanceService} from '../../../services/prl-finance.service';
import {combineLatestAll, of, Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';

@Component({
    selector: 'prl-add-payment-request',
    standalone: false,
    templateUrl: './prl-add-payment-request.component.html',
    styleUrl: './prl-add-payment-request.component.scss',
})
export class PrlAddPaymentRequestComponent implements MxCustomizer<MxFormCustomizerEvent>, OnBoFormReady, OnBoFormMetadataReady, OnInit {

    protected cancelButton: MxButtonModel;
    protected form: FormGroup;

    private _destroy$$: Subject<null> = new Subject<null>();

    private destroy$$: Subject<null> = new Subject<null>();
    private merchantData: AffiliateBalanceMxObj = null;

    private okString = 'OK';
    private defaultAffiliateId: number = null;

    constructor(private readonly financeService: PrlFinanceService,
                private readonly translateService: PrlTranslateService) {
    }

    ngOnInit(): void {
        this.cancelButton = {
            label: () => this.translateService.getTranslatedLabel('label.cancel'),
            tooltip: () => this.translateService.getTranslatedLabel('label.cancel'),
            className: 'mxs-button--secondary',
            clickHandler: this.clearMerchant
        };
        this.merchantData = this.financeService.getSelectedMerchant();
        if (this.merchantData) {
            of(this.financeService.getPaymentRequestProperties(this.merchantData.merchantId))
                .pipe(takeUntil(this.destroy$$), combineLatestAll())
                .subscribe((response) => {
                    this.financeService.setSelectedMerchantProperties(response[0]);
                });
        } else {
            this.financeService.closePaymentRequest.subscribe(() => {
                of(
                    this.financeService.getPaymentRequestProperties()
                ).pipe(takeUntil(this.destroy$$), combineLatestAll())
                    .subscribe((response) => {
                        this.financeService.setTotalPaymentRequestProperties(response[0]);
                    });
            });
            of(this.financeService.getPaymentRequestProperties())
                .pipe(takeUntil(this.destroy$$), combineLatestAll())
                .subscribe((response) => {
                    this.financeService.setTotalPaymentRequestProperties(response[0]);
                });
        }
    }

    boOnMetadataReady(metaData: ObjectMetaData): ObjectMetaData {
        return ObjectMetaData.modifyFields(metaData,
            field => {
                if (field.name === 'affiliateId') {
                    this.defaultAffiliateId = field.defaultValue as number;
                }
                if (field.name === 'idNumber'
                        && !this.financeService.getTotalPaymentRequestProperties()?.showIdentificationNumber) {
                    field.hidden = true;
                    field.validation.required = false;
                }
                if ((field.name === 'vatNumber' || field.name === 'vatPayer')
                        && !this.financeService.getTotalPaymentRequestProperties()?.showVatNumber) {
                    field.hidden = true;
                }
                return field;
            });
    }

    boOnFormReady(form: FormGroup): void {
        this.form = form;
        if (this.merchantData) {
            form.controls.merchantId.setValue(this.merchantData.merchantId);
        }
            let amount = form.controls.amount;
        const maxValue = this.merchantData
            ? this.merchantData.endingBalance
            : this.financeService.getTotalBalance().openingBalanceRounded;
        form.controls.amount.setValidators([Validators.required,
            Validators.min(this.financeService.getTotalPaymentRequestProperties().threshold),
            Validators.max(maxValue)]
        );
        amount.updateValueAndValidity();
        const vatPayer = form.controls.vatPayer;
        const vatNumber = form.controls.vatNumber;
        vatNumber.disable();
        vatNumber.updateValueAndValidity();
        vatPayer.valueChanges.pipe(
            takeUntil(this._destroy$$)
        ).subscribe(value => {
            if (!value || value === false) {
                vatNumber.disable();
                vatNumber.setValue(null);
                vatNumber.clearValidators();
            } else {
                vatNumber.enable();
                vatNumber.setValidators([Validators.required, Validators.minLength(6)]);
            }
            vatNumber.updateValueAndValidity();
        });

        this.form.controls.affiliateId.setValue(this.defaultAffiliateId);
    }

    onCustomize(event: MxFormCustomizerEvent): void {
        event
            .customizeFieldsDataConfig({
                className: 'com.playtech.mexos.contract.finance.NewWithdrawalPrlObj'
            })
            .customizeBoFormConfig({
                restLink: '/finance/create-withdrawal',
                title: this.getTitle(),
                multipleColumns: true,
                notificationMsg: () => this.translateService.getTranslatedLabel('label.finance.payment.request.created.content')
            })
            .customizeProps({
                showNavigation: false,
                addPrefixToTitle: false,
                customNavigation: {
                    onCancel: () => {
                        this.clearMerchant();
                    },
                    onBack: () => {
                        this.clearMerchant();
                    },
                    onSubmitRedirect: () => {
                        this.clearMerchant();
                    }
                }
            })
            .customizeFormMode('new')
            .addCustomizer(this);
    }

    protected hasError = (): boolean => {
        let result = this.financeService.getTotalPaymentRequestProperties()
            && this.financeService.getTotalPaymentRequestProperties().createWithdrawalStatus !== 'OK';
        if (result) {
            return true;
        }
        if (this.merchantData && !this.financeService.getSelectedMerchantProperties()) {
            return true;
        }
        if (this.merchantData && this.financeService.getSelectedMerchantProperties()) {
            return result || this.financeService.getSelectedMerchantProperties().createWithdrawalStatus !== 'OK';
        }
        if (this.financeService.isNewPaymentRequest()) {
            return false;
        }
        return result;
    };

    protected getErrorLabel = (): string => {
        const start = 'label.errors.payment.request.';
        if (this.financeService.getTotalPaymentRequestProperties()
            && this.financeService.getTotalPaymentRequestProperties().createWithdrawalStatus !== this.okString) {
            return start + this.financeService.getTotalPaymentRequestProperties().createWithdrawalStatus;
        }

        if (this.financeService.getSelectedMerchantProperties()
            && this.financeService.getSelectedMerchantProperties().createWithdrawalStatus !== this.okString) {
            return start + this.financeService.getSelectedMerchantProperties().createWithdrawalStatus;
        }

        return '';
    };

    private clearMerchant = () => {
        this.financeService.setNewPaymentRequest(false);
        this.financeService.setSelectedMerchant(null);
        this.financeService.setSelectedMerchantProperties(null);
        this.financeService.closePaymentRequest.emit();
    };

    private getTitle = (): string => {
        return this.merchantData
            ? this.translateService.getTranslatedLabel('label.finance.transactions.merchant') + ': ' + this.merchantData['merchantName']
            : '';
    };
}
